import { useForm } from "@inertiajs/react";
import {
  type FormEvent,
  type InputHTMLAttributes,
  type ReactNode,
  useState,
} from "react";
import { toast } from "sonner";

import AuthCard from "@/components/AuthCard";
import Button from "@/components/Button";
import SearchableSelect, {
  type SelectOption,
} from "@/components/SearchableSelect";
import { registrations } from "@/routes";

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink placeholder:text-ink/40 shadow-none focus:outline-none focus:ring-0 focus:shadow-none";

const DOCUMENT_OPTIONS: SelectOption[] = [
  { value: "run", label: "RUT" },
  { value: "other", label: "Pasaporte" },
];

const SEX_OPTIONS: SelectOption[] = [
  { value: "mujer", label: "Mujer" },
  { value: "hombre", label: "Hombre" },
];

// "Cursos para:" — only shown for Los Tilos (mujer) registrations.
const LOS_TILOS_CATEGORY_OPTIONS: SelectOption[] = [
  { value: "senora", label: "Señoras" },
  { value: "universitaria", label: "Universitarias" },
  { value: "numeraria_auxiliar", label: "Numerarias Auxiliares" },
  { value: "agregada", label: "Agregadas" },
  { value: "numeraria", label: "Numerarias" },
];
const INITIAL_FORM = {
  document: "run",
  rut: "",
  email: "",
  email_confirmation: "",
  phone_number: "",
  first_name: "",
  last_name: "",
  second_last_name: "",
  birth_date: "",
  sex: "",
  user_category: "",
  address: "",
  commune: "",
  city: "",
  password: "",
  password_confirmation: "",
};

const REQUIRED_FIELDS: [keyof typeof INITIAL_FORM, string][] = [
  ["rut", "Rut / Pasaporte"],
  ["email", "Correo electrónico"],
  ["email_confirmation", "Confirma tu correo electrónico"],
  ["phone_number", "Número de teléfono"],
  ["first_name", "Nombre"],
  ["last_name", "Apellido paterno"],
  ["second_last_name", "Apellido materno"],
  ["sex", "Sexo"],
  ["address", "Dirección"],
  ["password", "Contraseña"],
  ["password_confirmation", "Confirma tu contraseña"],
];

type Commune = { name: string; region: string };

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="mt-2 px-1 font-sans text-sm font-light text-ink/70 italic first:mt-0">
      {children}
    </p>
  );
}

function RequiredMark() {
  return <span className="text-xl text-ink/60">∗</span>;
}

/** Labels every field the same way the admin edit form does: the name sits
 * on the border as a legend, permanently — unlike a placeholder, it never
 * disappears once the user starts typing, so a filled field stays
 * identifiable. */
function Field({
  label,
  error,
  adornment,
  children,
}: {
  label: string;
  error?: string;
  adornment?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="w-full">
      <fieldset
        className={`relative rounded-full border-2 px-4 pb-2.5 focus-within:border-steel ${
          error ? "border-deep-red" : "border-ink"
        } ${adornment ? "pr-12" : ""}`}
      >
        <legend
          className={`truncate px-1.5 font-sans text-xs ${
            error ? "text-deep-red" : "text-ink/60"
          }`}
        >
          {label}
        </legend>
        {children}
        {adornment && (
          <span className="-translate-y-1/2 absolute top-1/2 right-5 flex items-center text-ink/60">
            {adornment}
          </span>
        )}
      </fieldset>
      {error && (
        <p className="mt-1 px-4 font-sans text-xs text-deep-red">{error}</p>
      )}
    </div>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

function PasswordInput(
  props: Omit<InputHTMLAttributes<HTMLInputElement>, "type">,
) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Input type={visible ? "text" : "password"} {...props} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="flex shrink-0 items-center text-ink/60 transition-colors hover:text-ink"
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        aria-pressed={visible}
        tabIndex={-1}
      >
        {visible ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

export default function RegistrationsNew({
  communes,
}: {
  communes: Commune[];
}) {
  const form = useForm({ ...INITIAL_FORM });

  // "Confirma tu correo electrónico" only exists client-side — shown as an
  // inline error while typing, and re-checked on submit.
  const emailMismatch =
    form.data.email_confirmation !== "" &&
    form.data.email_confirmation !== form.data.email;

  function setSex(sex: string) {
    form.setData((data) => ({
      ...data,
      sex,
      user_category: sex === "hombre" ? "la_cuesta" : "",
    }));
  }

  function setCommune(name: string) {
    const region = communes.find((c) => c.name === name)?.region;
    form.setData((data) => ({
      ...data,
      commune: name,
      city: region ?? data.city,
    }));
  }

  const cityOptions: SelectOption[] = [
    ...new Set(communes.map((c) => c.region)),
  ].map((region) => ({ value: region, label: region }));

  // The form is noValidate: validation errors surface as toasts (like the
  // server-side flash alerts) instead of the browser's per-field bubbles.
  function validationError(): string | null {
    const missing = REQUIRED_FIELDS.filter(([key]) => !form.data[key]).map(
      ([, label]) => label,
    );
    if (form.data.sex === "mujer" && !form.data.user_category) {
      missing.push("Cursos para");
    }
    if (missing.length > 0) {
      return `Completa los campos obligatorios: ${missing.join(", ")}.`;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.data.email)) {
      return "El correo electrónico no es válido.";
    }
    if (emailMismatch) return "Los correos electrónicos no coinciden.";
    if (form.data.password.length < 6) {
      return "La contraseña debe tener al menos 6 caracteres.";
    }
    if (form.data.password !== form.data.password_confirmation) {
      return "Las contraseñas no coinciden.";
    }
    return null;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const error = validationError();
    if (error) {
      toast.error(error, {
        className: "border border-deep-red/40 bg-deep-red/10 text-deep-red",
        duration: 5000,
      });
      return;
    }
    form.transform(({ email_confirmation: _, ...data }) => ({
      registration: data,
    }));
    form.post(registrations.create().url);
  }

  return (
    <AuthCard title="Registro" subtitle="Completa aquí tus datos">
      <form
        onSubmit={submit}
        noValidate
        className="mx-auto flex max-w-xl flex-col gap-4"
      >
        <SectionLabel>Datos de contacto</SectionLabel>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Tipo de documento">
            <SearchableSelect
              value={form.data.document}
              onChange={(value) => form.setData("document", value)}
              options={DOCUMENT_OPTIONS}
              placeholder="Tipo de documento"
              required
            />
          </Field>
          <Field label="Rut / Pasaporte" adornment={<RequiredMark />}>
            <Input
              name="rut"
              placeholder="Rut / Pasaporte"
              autoComplete="username"
              value={form.data.rut}
              onChange={(e) => form.setData("rut", e.target.value)}
              required
            />
          </Field>
        </div>

        <Field label="Correo electrónico" adornment={<RequiredMark />}>
          <Input
            name="email"
            type="email"
            placeholder="Correo electrónico válido"
            autoComplete="email"
            value={form.data.email}
            onChange={(e) => form.setData("email", e.target.value)}
            required
          />
        </Field>

        <Field
          label="Confirma tu correo electrónico"
          error={
            emailMismatch ? "Los correos electrónicos no coinciden" : undefined
          }
        >
          <Input
            name="email_confirmation"
            type="email"
            placeholder="Confirma tu correo electrónico"
            autoComplete="email"
            value={form.data.email_confirmation}
            onChange={(e) => form.setData("email_confirmation", e.target.value)}
            required
          />
        </Field>

        <Field
          label="Número de teléfono (ejemplo: +56912345678)"
          adornment={<RequiredMark />}
        >
          <Input
            name="phone_number"
            type="tel"
            placeholder="+56912345678"
            autoComplete="tel"
            value={form.data.phone_number}
            onChange={(e) => form.setData("phone_number", e.target.value)}
            required
          />
        </Field>

        <SectionLabel>Datos personales</SectionLabel>

        <Field label="Nombre" adornment={<RequiredMark />}>
          <Input
            name="first_name"
            placeholder="Nombre"
            autoComplete="given-name"
            value={form.data.first_name}
            onChange={(e) => form.setData("first_name", e.target.value)}
            required
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Apellido paterno" adornment={<RequiredMark />}>
            <Input
              name="last_name"
              placeholder="Apellido paterno"
              autoComplete="family-name"
              value={form.data.last_name}
              onChange={(e) => form.setData("last_name", e.target.value)}
              required
            />
          </Field>
          <Field label="Apellido materno" adornment={<RequiredMark />}>
            <Input
              name="second_last_name"
              placeholder="Apellido materno"
              value={form.data.second_last_name}
              onChange={(e) => form.setData("second_last_name", e.target.value)}
              required
            />
          </Field>
        </div>

        <Field label="Fecha de nacimiento">
          <Input
            type="date"
            value={form.data.birth_date}
            onChange={(e) => form.setData("birth_date", e.target.value)}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Sexo">
            <SearchableSelect
              value={form.data.sex}
              onChange={setSex}
              options={SEX_OPTIONS}
              placeholder="Sexo"
              required
            />
          </Field>
          {/* La Cuesta (hombre) has a single fixed category — nothing to pick. */}
          {form.data.sex === "mujer" && (
            <Field label="Cursos para">
              <SearchableSelect
                value={form.data.user_category}
                onChange={(value) => form.setData("user_category", value)}
                options={LOS_TILOS_CATEGORY_OPTIONS}
                placeholder="Cursos para:"
                required
              />
            </Field>
          )}
        </div>

        <SectionLabel>Datos de residencia</SectionLabel>

        <Field label="Dirección" adornment={<RequiredMark />}>
          <Input
            name="address"
            placeholder="Dirección"
            autoComplete="street-address"
            value={form.data.address}
            onChange={(e) => form.setData("address", e.target.value)}
            required
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Comuna">
            <SearchableSelect
              value={form.data.commune}
              onChange={setCommune}
              placeholder="Comuna"
              options={communes.map((c) => ({ value: c.name, label: c.name }))}
            />
          </Field>
          <Field label="Ciudad">
            <SearchableSelect
              value={form.data.city}
              onChange={(value) => form.setData("city", value)}
              options={cityOptions}
              placeholder="Ciudad"
            />
          </Field>
        </div>

        <SectionLabel>
          Crea tu contraseña para acceder a nuestro sitio
        </SectionLabel>

        <Field label="Contraseña">
          <PasswordInput
            name="password"
            placeholder="Contraseña"
            autoComplete="new-password"
            minLength={6}
            value={form.data.password}
            onChange={(e) => form.setData("password", e.target.value)}
            required
          />
        </Field>

        <Field label="Confirma tu contraseña">
          <PasswordInput
            name="password_confirmation"
            placeholder="Confirma tu contraseña"
            autoComplete="new-password"
            minLength={6}
            value={form.data.password_confirmation}
            onChange={(e) =>
              form.setData("password_confirmation", e.target.value)
            }
            required
          />
        </Field>

        <Button
          type="submit"
          variant="danger"
          disabled={form.processing}
          className="mt-2 w-full"
        >
          Registrarse
        </Button>
      </form>
    </AuthCard>
  );
}

function EyeIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12s3.75-7.5 9.75-7.5 9.75 7.5 9.75 7.5-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
      />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.22A10.48 10.48 0 0 0 2.25 12s3.75 7.5 9.75 7.5c1.66 0 3.18-.4 4.52-1.05M6.6 6.6A9.9 9.9 0 0 1 12 4.5c6 0 9.75 7.5 9.75 7.5a13.2 13.2 0 0 1-2.16 3.02M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2"
      />
    </svg>
  );
}
